import { useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';

import { useWorkerJobPositionsService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';

import { useJobPositionId } from './use-job-position-id';
import { jobApplicationAtom, jobPositionApplyStatusAtom } from '../components/job-position/job-position.atoms';

export const useJobApplyStatus = () => {
  const jobPositionId = useJobPositionId();
  const { user } = useAuth();
  const workerJobPositionsService = useWorkerJobPositionsService();

  const [jobPositionApplyStatus, setJobPositionApplyStatus] = useAtom(jobPositionApplyStatusAtom);
  const [jobApplication, setJobApplication] = useAtom(jobApplicationAtom);

  const handleFetchJobPositionApplyStatus = useCallback(async () => {
    try {
      if (user?.id && jobPositionId) {
        const response = await workerJobPositionsService.jobApplyStatus({
          worker_id: user?.id,
          job_position_id: jobPositionId,
        });
        const hasAlreadyApplied = response.data && response.data?.status?.key !== 'worker_jp_proposed';
        setJobPositionApplyStatus(hasAlreadyApplied);
        setJobApplication(response.data);
      }
    } catch (error) {
      console.error(error, 'Error on getting job position apply status');
    }
  }, [jobPositionId, setJobApplication, setJobPositionApplyStatus, user?.id, workerJobPositionsService]);
  useEffect(() => {
    handleFetchJobPositionApplyStatus();
  }, [handleFetchJobPositionApplyStatus]);
  return {
    jobPositionApplyStatus,
    jobApplication,
    handleFetchJobPositionApplyStatus,
  };
};

/** What a worker's application to one position is, as far as the apply confirmation cares. */
export type ApplyStatusOutcome =
  | { kind: 'already_applied'; jobPositionId: string }
  | { kind: 'proposed'; jobPositionId: string }
  | { kind: 'not_applied'; jobPositionId: string }
  | { kind: 'failed'; jobPositionId: string };

type ApplyStatusService = {
  jobApplyStatus: (args: {
    worker_id: string;
    job_position_id: string;
  }) => Promise<{ data?: { status_key?: string; status?: { key?: string } } | null }>;
};

/**
 * Reads whether the worker already has an application for this position, with an explicit
 * outcome and the position it was asked for. It never throws.
 *
 * `useJobApplyStatus` above writes a global atom that carries no position id and swallows
 * errors, so a caller cannot tell "not applied" from "could not check", nor a stale answer for
 * another position from the current one. The apply confirmation needs both (05/10/2026: 1,079
 * POST /apply answered 400 in 202 sessions since 01/10, 142 of them workers already applied who
 * came back from login with `?apply=true` and met a confirmation whose button did nothing).
 *
 * 200 with `worker_jp_proposed` is `proposed`; any other 200, a discarded one included, is
 * `already_applied`; 404 is `not_applied`; everything else, missing ids included, is `failed`.
 */
/** Ceiling on the check: past it the outcome is `failed` and the confirmation stays usable. */
export const APPLY_STATUS_TIMEOUT_MS = 8_000;

export const readApplyStatus = async (
  service: ApplyStatusService,
  workerId: string | undefined,
  jobPositionId: string,
  timeoutMs = APPLY_STATUS_TIMEOUT_MS
): Promise<ApplyStatusOutcome> => {
  if (!workerId || !jobPositionId) return { kind: 'failed', jobPositionId };
  // The shared axios instance has no timeout: a check that hung would keep the apply button
  // loading for ever (Codex review, 05/10/2026). A check that does not answer is a failed check.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('apply status check timed out')), timeoutMs);
  });
  try {
    const response = await Promise.race([
      service.jobApplyStatus({ worker_id: workerId, job_position_id: jobPositionId }),
      deadline,
    ]);
    if (!response?.data) return { kind: 'not_applied', jobPositionId };
    const statusKey = response.data.status_key ?? response.data.status?.key;
    return { kind: statusKey === 'worker_jp_proposed' ? 'proposed' : 'already_applied', jobPositionId };
  } catch (error) {
    // The data-services instance rejects with `{ code }`; an axios error that never got through
    // the interceptor still carries `response.status`.
    const failed = error as { code?: unknown; response?: { status?: number } } | null;
    const status = typeof failed?.code === 'number' ? failed.code : failed?.response?.status;
    return { kind: status === 404 ? 'not_applied' : 'failed', jobPositionId };
  } finally {
    if (timer) clearTimeout(timer);
  }
};
